import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI, subscriptionAPI } from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch subscription plan and merge into user object
  const fetchAndMergePlan = useCallback(async (userData) => {
    try {
      const subRes = await subscriptionAPI.getCurrent();
      const plan = subRes.data?.plan || 'demo';
      const merged = { ...userData, plan };
      setUser(merged);
      localStorage.setItem('user', JSON.stringify(merged));
      return merged;
    } catch (err) {
      // If subscription fetch fails, default to demo
      const merged = { ...userData, plan: 'demo' };
      setUser(merged);
      localStorage.setItem('user', JSON.stringify(merged));
      return merged;
    }
  }, []);

  // Public method to refresh subscription status
  const refreshSubscription = useCallback(async () => {
    if (!user) return;
    await fetchAndMergePlan(user);
  }, [user, fetchAndMergePlan]);

  // Auto-refresh subscription: poll every 60s + on window focus
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem('token');
    if (!token) return;

    const refresh = async () => {
      try {
        const subRes = await subscriptionAPI.getCurrent();
        const plan = subRes.data?.plan || 'demo';
        setUser(prev => {
          if (!prev || prev.plan === plan) return prev;
          const updated = { ...prev, plan };
          localStorage.setItem('user', JSON.stringify(updated));
          return updated;
        });
      } catch {}
    };

    const interval = setInterval(refresh, 60000);
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [!!user]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      // Verify token is still valid and refresh plan
      authAPI.getMe()
        .then(async (res) => {
          await fetchAndMergePlan(res.data);
        })
        .catch(() => {
          logout();
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const res = await authAPI.login({ email, password, timezone_offset: new Date().getTimezoneOffset() });
    localStorage.setItem('token', res.data.access_token);
    localStorage.setItem('user', JSON.stringify(res.data.user));
    setUser(res.data.user);
    await fetchAndMergePlan(res.data.user);
    return res.data;
  };

  const register = async (name, email, password, captchaData = {}) => {
    const res = await authAPI.register({ name, email, password, timezone_offset: new Date().getTimezoneOffset(), ...captchaData });
    localStorage.setItem('token', res.data.access_token);
    localStorage.setItem('user', JSON.stringify(res.data.user));
    setUser(res.data.user);
    await fetchAndMergePlan(res.data.user);
    return res.data;
  };

  const googleLogin = async (accessToken) => {
    const res = await authAPI.googleAuth(accessToken);
    localStorage.setItem('token', res.data.access_token);
    localStorage.setItem('user', JSON.stringify(res.data.user));
    setUser(res.data.user);
    await fetchAndMergePlan(res.data.user);
    return res.data;
  };

  const yandexLogin = async (token) => {
    localStorage.setItem('token', token);
    const res = await authAPI.getMe();
    localStorage.setItem('user', JSON.stringify(res.data));
    setUser(res.data);
    await fetchAndMergePlan(res.data);
    return res.data;
  };

  const vkLogin = async (token) => {
    localStorage.setItem('token', token);
    const res = await authAPI.getMe();
    localStorage.setItem('user', JSON.stringify(res.data));
    setUser(res.data);
    await fetchAndMergePlan(res.data);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, googleLogin, yandexLogin, vkLogin, logout, updateUser, refreshSubscription }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
